import React, { useState, useEffect, useRef } from 'react';
import API from '../api';
import { FaPaperPlane, FaRobot, FaUser, FaRegClock, FaSyncAlt, FaTrash, FaPlusCircle } from 'react-icons/fa';
import './AssistantIA.css';

function AssistantIA() {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [showConversations, setShowConversations] = useState(true);
  const [isNewConversation, setIsNewConversation] = useState(true);
  const chatEndRef = useRef(null);

  // Récupération des données utilisateur
  useEffect(() => {
    async function fetchUserData() {
      try {
        const userResponse = await API.get('/api/auth/user-info');
        setUserData(userResponse.data);
      } catch (err) {
        console.error("Erreur lors du chargement des données utilisateur:", err);
      }
    }

    async function fetchConversations() {
      try {
        setLoading(true);
        const response = await API.get('/api/ia/conversations');
        setConversations(response.data);
        
        // Créer une nouvelle conversation à chaque ouverture au lieu de charger la plus récente
        createNewConversation();
      } catch (err) {
        console.error("Erreur lors du chargement des conversations:", err);
        setError("Impossible de charger les conversations. Veuillez réessayer.");
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
    fetchConversations();
  }, []);

  // Défiler vers le bas lorsque de nouveaux messages sont ajoutés
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  // Charger une conversation spécifique
  const loadConversation = async (conversationId) => {
    try {
      setLoading(true);
      setError('');
      const response = await API.get(`/api/ia/conversations/${conversationId}`);
      setChatHistory(response.data.messages || []);
      setActiveConversation(conversationId);
      setIsNewConversation(false); // Ce n'est plus une nouvelle conversation
    } catch (err) {
      setError("Erreur lors du chargement de la conversation");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Créer une nouvelle conversation
  const createNewConversation = () => {
    setChatHistory([]);
    setActiveConversation(null);
    setIsNewConversation(true); // Marquer comme nouvelle conversation
  };

  // Supprimer une conversation
  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation(); // Empêcher le clic de propager au parent
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette conversation ?")) {
      try {
        await API.delete(`/api/ia/conversations/${conversationId}`);
        
        // Mettre à jour la liste des conversations
        setConversations(conversations.filter(conv => conv.id !== conversationId));
        
        // Si c'était la conversation active, réinitialiser
        if (activeConversation === conversationId) {
          setChatHistory([]);
          setActiveConversation(null);
        }
      } catch (err) {
        setError("Erreur lors de la suppression de la conversation");
        console.error(err);
      }
    }
  };

  // Envoyer un message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    const userMessage = message;
    setMessage('');
    setIsNewConversation(false);
    
    // Ajouter le message de l'utilisateur à l'historique local
    setChatHistory(prev => [...prev, {
      content: userMessage,
      is_user: true,
      timestamp: new Date().toISOString()
    }]);
    
    try {
      setLoading(true);
      setError('');
      
      const response = await API.post('/api/ia/chat', {
        message: userMessage,
        conversation_id: activeConversation
      });
      
      // Ajouter la réponse de l'assistant à l'historique
      setChatHistory(prev => [...prev, {
        content: response.data.response,
        is_user: false,
        timestamp: new Date().toISOString()
      }]);
      
      // Mettre à jour l'ID de conversation si c'est une nouvelle
      if (!activeConversation) {
        setActiveConversation(response.data.conversation_id);
        
        // Rafraîchir la liste des conversations
        const convsResponse = await API.get('/api/ia/conversations');
        setConversations(convsResponse.data);
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403 && detail?.includes("Limite démo")) {
        setError(detail);
      } else {
        console.error("Erreur lors de l'envoi du message:", err);
        setError("Erreur lors de l'envoi du message");
        // Ajouter un message d'erreur à l'historique
        setChatHistory(prev => [...prev, {
          content: "Désolé, une erreur s'est produite lors du traitement de votre message.",
          is_user: false,
          timestamp: new Date().toISOString()
        }]);
      }
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  };

  // Formater les dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Suggestions de messages rapides
  const quickSuggestions = [
    "Comment puis-je optimiser mes extractions?",
    "Analysez mes données de prospection récentes",
    "Générez un script d'appel pour mes nouveaux prospects",
    "Quels secteurs je n'ai pas assez démarché ?"
  ];

  return (
    <div className="chatbot-container">
      <div className="chatbot-sidebar">
        <button 
          className="new-conversation-btn"
          onClick={createNewConversation}
        >
          <FaPlusCircle /> Nouvelle conversation
        </button>
        
        {/* Affichage permanent de l'historique des conversations (sans condition) */}
        <div className="conversations-list">
          <h3>Historique des conversations</h3>
          {conversations.length === 0 && (
            <p className="no-conversations">Aucune conversation enregistrée</p>
          )}
          {conversations.map(conv => (
            <div 
              key={conv.id} 
              className={`conversation-item ${activeConversation === conv.id ? 'active' : ''}`}
              onClick={() => loadConversation(conv.id)}
            >
              <p className="conversation-preview">{conv.preview || "Conversation"}</p>
              <p className="conversation-date">{formatDate(conv.last_updated)}</p>
              <button 
                className="delete-conversation-btn"
                onClick={(e) => deleteConversation(conv.id, e)}
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
        
        {userData && (
          <div className="user-data-summary">
            <h3>Vos statistiques</h3>
            <p><strong>Activité:</strong> {userData.activite || "Non spécifiée"}</p>
          </div>
        )}
      </div>
      
      <div className="chatbot-main">
        <div className="chat-header">
          <h2><FaRobot /> ProspX - Assistant IA Commercial</h2>
          {activeConversation && (
            <button className="refresh-btn" onClick={() => loadConversation(activeConversation)}>
              <FaSyncAlt />
            </button>
          )}
        </div>
        
        <div className="chat-messages">
          {chatHistory.length === 0 || isNewConversation ? (
            <div className="welcome-message">
              <h3>Bienvenue dans l'Assistant IA Commercial ProspX</h3>
              <p>Je suis ProspX, votre assistant de prospection commercial. Je peux vous aider à analyser vos leads, générer des scripts de prospection et optimiser votre stratégie commerciale.</p>
              <div className="quick-suggestions">
                <h4>Essayez ces demandes:</h4>
                {quickSuggestions.map((suggestion, index) => (
                  <button 
                    key={index} 
                    className="suggestion-btn"
                    onClick={() => {
                      setMessage(suggestion);
                      // Trigger sendMessage programmatically
                      document.getElementById('chat-form').dispatchEvent(
                        new Event('submit', { cancelable: true, bubbles: true })
                      );
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          
          {chatHistory.map((msg, index) => (
            <div key={index} className={`message ${msg.is_user ? 'user-message' : 'assistant-message'}`}>
              <div className="message-icon">
                {msg.is_user ? <FaUser /> : <FaRobot />}
              </div>
              <div className="message-content">
                <div className="message-text">{msg.content}</div>
                <div className="message-time">
                  <FaRegClock /> {formatDate(msg.timestamp)}
                </div>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        
        <form id="chat-form" className="chat-input-form" onSubmit={sendMessage}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tapez votre message ici..."
            disabled={loading}
          />
          <button type="submit" disabled={loading || !message.trim()}>
            {loading ? <div className="spinner"></div> : <FaPaperPlane />}
          </button>
        </form>
        <div className="disclaimer-message">
          L'IA peut faire des erreurs. Envisagez de vérifier les informations importantes.
        </div>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
}

export default AssistantIA;
